// Форма создания долга. Поля адаптивны под выбранный тип:
// — личный заём (personal) не показывает % и payments_left;
// — рассрочка (installment) показывает payments_left, но не %;
// — uzum_zero (беспроцентный) — без %;
// — bank/uzum/company — полный набор.
//
// Дополнительные поля скрыты в раскрывающемся блоке "Подробнее".

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { api } from '../api/client';
import { DEBT_KINDS } from '../lib/debt-kinds';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { SheetCta } from './SheetCta';
import { useBackButton, useMainButton } from '../lib/use-main-button';
import { ChevronDownIcon } from './icons';

type Props = {
  onClose: () => void;
};

type DebtKind = 'bank' | 'uzum' | 'uzum_zero' | 'installment' | 'overdue' | 'company' | 'personal';

// Универсальные подписи без привязки к Uzum.
type KindOption = {
  value: DebtKind;
  label: string;
  hint: string;
  // Какие поля показывать для этого типа.
  fields: {
    monthly: boolean;
    rate: boolean;
    paymentsLeft: boolean;
  };
  // Подходящие лейблы поля «название» для подсказки.
  titlePlaceholder: string;
  lenderPlaceholder: string;
};

const KIND_OPTIONS: KindOption[] = [
  {
    value: 'bank',
    label: 'Кредит с процентами',
    hint: 'Банк, потребительский, ипотека',
    fields: { monthly: true, rate: true, paymentsLeft: true },
    titlePlaceholder: 'Например: На ремонт',
    lenderPlaceholder: 'Капитал банк',
  },
  {
    value: 'uzum',
    label: 'Микрозайм',
    hint: 'Короткий заём, обычно с высокой ставкой',
    fields: { monthly: true, rate: true, paymentsLeft: true },
    titlePlaceholder: 'Например: Срочный заём',
    lenderPlaceholder: 'Uzum, Kapital24, Hofiz...',
  },
  {
    value: 'uzum_zero',
    label: 'Беспроцентный заём',
    hint: '0% ставка, фиксированные платежи',
    fields: { monthly: true, rate: false, paymentsLeft: true },
    titlePlaceholder: 'Например: Заём 0%',
    lenderPlaceholder: 'Uzum 0%, Hamkorbank 0%...',
  },
  {
    value: 'installment',
    label: 'Рассрочка магазина',
    hint: 'Покупка с фикс. графиком, без явных %',
    fields: { monthly: true, rate: false, paymentsLeft: true },
    titlePlaceholder: 'Например: Стиральная машина',
    lenderPlaceholder: 'Mediapark, Idea, Texnomart...',
  },
  {
    value: 'overdue',
    label: 'Просрочка',
    hint: 'Долг с истёкшим сроком',
    fields: { monthly: true, rate: false, paymentsLeft: false },
    titlePlaceholder: 'Например: Просрочка по карте',
    lenderPlaceholder: 'Откуда долг',
  },
  {
    value: 'company',
    label: 'Долг компании',
    hint: 'B2B, контрагенту',
    fields: { monthly: false, rate: true, paymentsLeft: false },
    titlePlaceholder: 'Например: Поставщику',
    lenderPlaceholder: 'Название компании',
  },
  {
    value: 'personal',
    label: 'Личный заём',
    hint: 'Другу, родственнику — без %',
    fields: { monthly: false, rate: false, paymentsLeft: false },
    titlePlaceholder: 'Например: На свадьбу',
    lenderPlaceholder: 'Имя человека',
  },
];

function KindIcon({ kind, size }: { kind: DebtKind; size: number }): JSX.Element {
  const Icon = DEBT_KINDS[kind].Icon;
  return <Icon size={size} />;
}

const KIND_BY_VALUE: Record<DebtKind, KindOption> = Object.fromEntries(
  KIND_OPTIONS.map((o) => [o.value, o]),
) as Record<DebtKind, KindOption>;

function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!cleaned) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

export function DebtFormSheet({ onClose }: Props): JSX.Element {
  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [kind, setKind] = useState<DebtKind>('bank');
  const [title, setTitle] = useState('');
  const [lender, setLender] = useState('');
  const [principal, setPrincipal] = useState('');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [ratePct, setRatePct] = useState('');
  const [paymentsLeft, setPaymentsLeft] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const lendersListId = useId();
  const lendersQ = useQuery({
    queryKey: ['lenders'],
    queryFn: () => api.lenders().then((r) => r.lenders),
    staleTime: 60_000,
  });

  const opt = KIND_BY_VALUE[kind];
  const principalMinor = parseAmountToMinor(principal);
  const monthlyMinor = monthlyPayment ? parseAmountToMinor(monthlyPayment) : null;
  const isValid = !!title.trim() && principalMinor !== null && principalMinor > 0n;

  const queryClient = useQueryClient();
  const toast = useToast();
  const createMut = useMutation({
    mutationFn: () => {
      if (!principalMinor) throw new Error('Invalid principal');
      const rateNum = opt.fields.rate && ratePct ? Number(ratePct) / 100 : undefined;
      const paymentsNum =
        opt.fields.paymentsLeft && paymentsLeft ? Number(paymentsLeft) : undefined;
      return api.createDebt({
        kind,
        title: title.trim(),
        lender: lender.trim() || undefined,
        principal_minor: principalMinor.toString(),
        monthly_payment_minor:
          opt.fields.monthly && monthlyMinor ? monthlyMinor.toString() : undefined,
        nominal_rate: rateNum && Number.isFinite(rateNum) ? rateNum : undefined,
        payments_left: paymentsNum && Number.isFinite(paymentsNum) ? paymentsNum : undefined,
        due_date: dueDate || undefined,
        note: note.trim() || undefined,
      });
    },
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      queryClient.invalidateQueries({ queryKey: ['lenders'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось создать долг')),
  });

  const cta = useMainButton({
    text: 'Создать долг',
    onClick: () => isValid && createMut.mutate(),
    enabled: isValid && !createMut.isPending,
    progress: createMut.isPending,
  });

  const formula = useMemo(() => {
    if (!opt.fields.rate || !ratePct) return null;
    const r = Number(ratePct) / 100;
    if (!Number.isFinite(r) || r <= 0) return null;
    if (!principalMinor) return null;
    const monthlyInterest = (principalMinor * BigInt(Math.round(r * 1_000_000))) / 12_000_000n;
    return monthlyInterest;
  }, [opt.fields.rate, ratePct, principalMinor]);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">Новый долг</div>

        {/* Тип — карточный selector */}
        <div className="form-field">
          <span className="form-label">Тип</span>
          <div className="kind-cards">
            {KIND_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`kind-card ${kind === o.value ? 'active' : ''}`}
                onClick={() => {
                  haptic('light');
                  setKind(o.value);
                }}
              >
                <span className="kind-card-icon">
                  <KindIcon kind={o.value} size={17} />
                </span>
                <span className="kind-card-label">{o.label}</span>
              </button>
            ))}
          </div>
          <span className="form-hint">{opt.hint}</span>
        </div>

        {/* Кредитор */}
        <label className="form-field">
          <span className="form-label">Кому должен</span>
          <input
            type="text"
            value={lender}
            onChange={(e) => setLender(e.target.value)}
            className="form-input"
            placeholder={opt.lenderPlaceholder}
            list={lendersListId}
            autoComplete="off"
          />
          <datalist id={lendersListId}>
            {(lendersQ.data ?? []).map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
          <span className="form-hint">Имя человека или название организации</span>
        </label>

        {/* Описание */}
        <label className="form-field">
          <span className="form-label">Описание</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input"
            placeholder={opt.titlePlaceholder}
            autoFocus
          />
        </label>

        {/* Сумма */}
        <label className="form-field">
          <span className="form-label">Сумма долга, UZS</span>
          <input
            type="text"
            inputMode="decimal"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            className={`form-input ${principal && principalMinor === null ? 'invalid' : ''}`}
            placeholder="5 000 000"
          />
          {principal && principalMinor === null ? (
            <span className="form-hint error">Введи положительное число</span>
          ) : null}
        </label>

        {/* Ежемесячный платёж — для всех кроме personal/company */}
        {opt.fields.monthly ? (
          <label className="form-field">
            <span className="form-label">Платёж в месяц</span>
            <input
              type="text"
              inputMode="decimal"
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(e.target.value)}
              className="form-input"
              placeholder="500 000"
            />
            <span className="form-hint">Регулярный платёж по графику. Можно оставить пустым.</span>
          </label>
        ) : null}

        {/* Ставка и платежей — conditional, в одной строке если оба */}
        {opt.fields.rate || opt.fields.paymentsLeft ? (
          <div className={opt.fields.rate && opt.fields.paymentsLeft ? 'row-2' : ''}>
            {opt.fields.rate ? (
              <label className="form-field">
                <span className="form-label">Ставка, % в год</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={ratePct}
                  onChange={(e) => setRatePct(e.target.value)}
                  className="form-input"
                  placeholder="27"
                />
                {formula ? <span className="form-hint">~{formatMinor(formula)} %/мес</span> : null}
              </label>
            ) : null}

            {opt.fields.paymentsLeft ? (
              <label className="form-field">
                <span className="form-label">Платежей</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={paymentsLeft}
                  onChange={(e) => setPaymentsLeft(e.target.value)}
                  className="form-input"
                  placeholder="12"
                />
                <span className="form-hint">Сколько осталось</span>
              </label>
            ) : null}
          </div>
        ) : null}

        {/* Срок возврата — единственное поле, общее для всех типов: дедлайн есть
            и у займа у знакомого, и у беспроцентной рассрочки. */}
        <label className="form-field">
          <span className="form-label">Срок возврата</span>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="form-input"
          />
          <span className="form-hint">
            {kind === 'uzum_zero'
              ? 'Дата, после которой начнут капать проценты. Попадёт в блок «Окно».'
              : 'Когда вернуть целиком. Необязательно.'}
          </span>
        </label>

        {/* Подробнее (раскрывающийся блок) */}
        <button
          type="button"
          className="advanced-toggle"
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          <ChevronDownIcon
            size={14}
            style={{ transform: advancedOpen ? 'none' : 'rotate(-90deg)' }}
          />
          <span>{advancedOpen ? 'Скрыть подробности' : 'Добавить заметку'}</span>
        </button>

        {advancedOpen ? (
          <label className="form-field">
            <span className="form-label">Заметка</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="form-input form-textarea"
              rows={2}
              placeholder="Номер договора, условия, что угодно"
            />
          </label>
        ) : null}

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={createMut.isPending}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

function formatMinor(minor: bigint): string {
  const major = Number(minor / 100n);
  if (major === 0) return '0';
  if (major >= 1_000_000) return `${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${(major / 1_000).toFixed(0)}k`;
  return major.toString();
}
